// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Summary rules editor: Rollup rows and their sources (blueprint-sets Req 6.1, 6.3).
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select, { type SelectProps } from '@cloudscape-design/components/select';
import {
  type MemberPath,
  type RollupAggregate,
  type RollupRule,
  type RollupSource,
  type ValidationErrors,
} from '@bda/shared';
import {
  AGGREGATES,
  BY_MEMBER,
  type Ctx,
  ErrorText,
  isMissingRef,
  isNumeric,
  memberOptions,
  MISSING_REFERENCE_LABEL,
  NO_GROUPING,
  pathLabel,
  pathOption,
  removeAt,
  replaceAt,
  rowLabel,
  RuleRow,
  SEGMENTS_VALUE,
  selectedMember,
  selectedPath,
  sourceError,
} from './summaryRulesShared';

// ---------- Rollups ----------

const isPathSource = (s: RollupSource): s is { memberId: string; path: MemberPath } => !('segments' in s);

/** The default source of a Member for an aggregate: its first fitting field, or a document count. */
export function defaultSource(ctx: Ctx, memberId: string, aggregate: RollupAggregate): RollupSource {
  const paths = ctx.paths.get(memberId) ?? [];
  const p = aggregate === 'count' ? paths[0] : paths.find(isNumeric);
  if (p) return { memberId, path: p.path };
  return aggregate === 'count' ? { memberId, segments: true } : { memberId, path: '' };
}

function RollupSourceRow({
  source,
  rollup,
  rollupRow,
  rollupKey,
  index,
  count,
  ctx,
  onChange,
  onRemove,
}: {
  source: RollupSource;
  rollup: RollupRule;
  rollupRow: string;
  rollupKey: string;
  index: number;
  count: number;
  ctx: Ctx;
  onChange: (s: RollupSource) => void;
  onRemove: () => void;
}) {
  const row = `${rollupRow} source ${index + 1}`;
  const paths = ctx.paths.get(source.memberId) ?? [];
  const fieldOptions: SelectProps.Option[] = [
    ...(rollup.aggregate === 'count'
      ? [
          {
            value: SEGMENTS_VALUE,
            label: 'Number of documents',
            description: 'Segments matched to this blueprint',
          },
        ]
      : []),
    ...paths.filter((p) => rollup.aggregate === 'count' || isNumeric(p)).map(pathOption),
  ];
  const path = isPathSource(source) ? source.path : null;
  const selected = isPathSource(source)
    ? selectedPath(ctx, source.memberId, source.path)
    : {
        value: SEGMENTS_VALUE,
        label: 'Number of documents',
        description: 'Segments matched to this blueprint',
      };
  return (
    <ColumnLayout columns={3}>
      <FormField label={rowLabel(row, 'Blueprint')}>
        <Select
          selectedOption={selectedMember(ctx, source.memberId)}
          options={memberOptions(ctx)}
          disabled={ctx.readOnly}
          ariaLabel={`${row} blueprint`}
          onChange={(e) => onChange(defaultSource(ctx, e.detail.selectedOption.value!, rollup.aggregate))}
        />
      </FormField>
      <FormField
        label={rowLabel(row, 'Field')}
        errorText={sourceError(ctx.errors[`${rollupKey}.sources.${index}`], path)}
      >
        <Select
          selectedOption={selected}
          options={fieldOptions}
          placeholder="Choose a field"
          empty="This blueprint has no number fields."
          disabled={ctx.readOnly}
          ariaLabel={`${row} field`}
          onChange={(e) => {
            const v = e.detail.selectedOption.value!;
            onChange(
              v === SEGMENTS_VALUE
                ? { memberId: source.memberId, segments: true }
                : { memberId: source.memberId, path: v },
            );
          }}
        />
      </FormField>
      {!ctx.readOnly && (
        <Box padding={{ top: 'xl' }}>
          <Button
            variant="icon"
            iconName="remove"
            ariaLabel={`Remove ${row}`}
            disabled={count <= 1}
            onClick={onRemove}
          />
        </Box>
      )}
    </ColumnLayout>
  );
}

export function RollupRow({
  rule,
  index,
  ctx,
  onChange,
  onRemove,
}: {
  rule: RollupRule;
  index: number;
  ctx: Ctx;
  onChange: (r: RollupRule) => void;
  onRemove: () => void;
}) {
  const row = `Rollup ${index + 1}`;
  const key = `rollups.${index}`;
  const missing = rollupMissing(rule, index, ctx.errors);
  const memberIds = [...new Set(rule.sources.map((s) => s.memberId))];
  const groupMember = memberIds.length === 1 ? memberIds[0]! : null;
  const groupOptions: SelectProps.Option[] = [
    { value: NO_GROUPING, label: 'No grouping' },
    { value: BY_MEMBER, label: 'By blueprint' },
    ...(groupMember
      ? (ctx.paths.get(groupMember) ?? [])
          .filter((p) => p.type === 'string')
          .map((p) => ({ ...pathOption(p), label: `By ${pathLabel(p.path)}` }))
      : []),
  ];
  const gb = rule.groupBy;
  const groupSelected: SelectProps.Option = !gb
    ? groupOptions[0]!
    : gb.kind === 'member'
      ? groupOptions[1]!
      : (groupOptions.find((o) => o.value === gb.path) ?? {
          value: gb.path,
          label: `By ${pathLabel(gb.path)}`,
          description: 'Not available',
        });
  const firstMember = ctx.members[0]?.blueprintId;
  return (
    <RuleRow
      title={rule.name.trim() || row}
      marker={missing ? MISSING_REFERENCE_LABEL : undefined}
      onRemove={onRemove}
      readOnly={ctx.readOnly}
    >
      <ColumnLayout columns={3}>
        <FormField label={rowLabel(row, 'Name')} errorText={ctx.errors[`${key}.name`]}>
          <Input
            value={rule.name}
            readOnly={ctx.readOnly}
            ariaLabel={`${row} name`}
            onChange={(e) => onChange({ ...rule, name: e.detail.value })}
          />
        </FormField>
        <FormField label={rowLabel(row, 'Aggregate')}>
          <Select
            selectedOption={AGGREGATES.find((a) => a.value === rule.aggregate)!}
            options={AGGREGATES}
            disabled={ctx.readOnly}
            ariaLabel={`${row} aggregate`}
            onChange={(e) => onChange({ ...rule, aggregate: e.detail.selectedOption.value as RollupAggregate })}
          />
        </FormField>
        <FormField
          label={rowLabel(row, 'Grouping')}
          description={groupMember ? undefined : 'Group by a field when every source is from one blueprint.'}
          errorText={sourceError(ctx.errors[`${key}.groupBy`], gb?.kind === 'field' ? gb.path : null)}
        >
          <Select
            selectedOption={groupSelected}
            options={groupOptions}
            disabled={ctx.readOnly}
            ariaLabel={`${row} grouping`}
            onChange={(e) => {
              const v = e.detail.selectedOption.value!;
              const { groupBy: _old, ...rest } = rule;
              onChange(
                v === NO_GROUPING
                  ? rest
                  : v === BY_MEMBER
                    ? { ...rest, groupBy: { kind: 'member' } }
                    : { ...rest, groupBy: { kind: 'field', path: v } },
              );
            }}
          />
        </FormField>
      </ColumnLayout>
      <Box variant="h5" padding="n">
        Sources
      </Box>
      <ErrorText>{ctx.errors[`${key}.sources`]}</ErrorText>
      {rule.sources.map((s, j) => (
        <RollupSourceRow
          key={j}
          source={s}
          rollup={rule}
          rollupRow={row}
          rollupKey={key}
          index={j}
          count={rule.sources.length}
          ctx={ctx}
          onChange={(next) => onChange({ ...rule, sources: replaceAt(rule.sources, j, next) })}
          onRemove={() => onChange({ ...rule, sources: removeAt(rule.sources, j) })}
        />
      ))}
      {!ctx.readOnly && firstMember && (
        <Button
          iconName="add-plus"
          ariaLabel={`Add a source to ${rule.name.trim() || row}`}
          onClick={() =>
            onChange({
              ...rule,
              sources: [...rule.sources, defaultSource(ctx, groupMember ?? firstMember, rule.aggregate)],
            })
          }
        >
          Add source
        </Button>
      )}
      <ErrorText>{ctx.errors[`${key}.id`]}</ErrorText>
    </RuleRow>
  );
}

/** Whether a source or the grouping of the rollup at `index` references a field missing from its Member. */
export function rollupMissing(rule: RollupRule, index: number, errors: ValidationErrors): boolean {
  const key = `rollups.${index}`;
  const sourceMissing = rule.sources.some((s, j) =>
    isMissingRef(errors[`${key}.sources.${j}`], isPathSource(s) ? s.path : null),
  );
  return (
    sourceMissing || isMissingRef(errors[`${key}.groupBy`], rule.groupBy?.kind === 'field' ? rule.groupBy.path : null)
  );
}
